// tab-manager.js - 탭 간 통신 및 충돌 관리
// Extracted from app.js

import { state, setState } from '../state.js';

/**
 * 탭 간 통신 채널
 * @type {BroadcastChannel}
 */
const tabChannel = new BroadcastChannel('scantodesk-tab');

/**
 * 탭 충돌 체크 및 처리
 * @returns {Promise<boolean>} true: 이 탭이 진행, false: 다른 탭으로 이동
 */
export function checkExistingTab() {
  return new Promise((resolve) => {
    let resolved = false;

    // 기존 탭에서 응답 받으면
    const onMessage = (event) => {
      if (event.data.type === 'ack_focus') {
        resolved = true;
        tabChannel.removeEventListener('message', onMessage);
        resolve(false); // 다른 탭이 있음
      }
    };
    tabChannel.addEventListener('message', onMessage);

    // 새 탭 열림 알림
    tabChannel.postMessage({ type: 'new_tab_opened' });

    // 500ms 내 응답 없으면 기존 탭 없음으로 판단
    setTimeout(() => {
      if (!resolved) {
        tabChannel.removeEventListener('message', onMessage);
        resolve(true); // 이 탭이 진행
      }
    }, 500);
  });
}

/**
 * 세션 변경 콜백 (외부에서 설정)
 * @type {Function|null}
 */
let onSessionChangedCallback = null;

/**
 * 세션 인계 콜백 (외부에서 설정)
 * 다른 탭에서 take_session 메시지를 받았을 때 호출
 * @type {Function|null}
 */
let onTakeSessionCallback = null;

/**
 * 세션 변경 콜백 설정
 * @param {Function} callback - 새 세션 정보가 있을 때 호출
 */
export function setOnSessionChanged(callback) {
  onSessionChangedCallback = callback;
}

/**
 * 세션 인계 콜백 설정
 * @param {Function} callback - (code: string) => Promise<boolean> 성공 여부 반환
 */
export function setOnTakeSession(callback) {
  onTakeSessionCallback = callback;
}

/**
 * 다른 탭에 세션 변경 알림
 */
export function notifySessionChanged() {
  tabChannel.postMessage({ type: 'session_changed' });
}

/**
 * 기존 탭으로 포커스 요청 처리
 */
export function setupTabChannelListener() {
  tabChannel.addEventListener('message', (event) => {
    if (event.data.type === 'new_tab_opened' && state.isActiveTab) {
      // 기존 탭: 포커스 시도 + 응답
      window.focus();
      tabChannel.postMessage({ type: 'ack_focus' });
    }

    if (event.data.type === 'request_focus') {
      window.focus();
    }

    // 다른 탭에서 새 세션으로 연결됨 - 이 탭도 새 세션 정보로 업데이트
    if (event.data.type === 'session_changed' && onSessionChangedCallback) {
      onSessionChangedCallback();
    }

    // 새 탭에서 세션 인계 요청 (QR 코드로 열린 새 탭 → 기존 탭)
    if (event.data.type === 'take_session' && event.data.code && onTakeSessionCallback) {
      onTakeSessionCallback(event.data.code).then((success) => {
        if (success) {
          tabChannel.postMessage({ type: 'session_taken' });
          window.focus();
        }
      });
    }
  });
}

/**
 * 다른 탭 사용 중 다이얼로그 표시
 * @param {HTMLElement} dialog - 다이얼로그 요소
 */
export function showOtherTabActiveDialog(dialog) {
  if (!dialog) return;

  dialog.classList.remove('hidden');

  // 기존 탭으로 이동 버튼
  const btnGoOtherTab = document.getElementById('btn-go-other-tab');
  if (btnGoOtherTab) {
    btnGoOtherTab.addEventListener('click', () => {
      // 기존 탭에 포커스 요청
      tabChannel.postMessage({ type: 'request_focus' });

      // 이 탭 닫기 시도
      window.close();

      // window.close()가 안 되면 안내 메시지로 변경
      setTimeout(() => {
        const title = dialog.querySelector('h3');
        const desc = dialog.querySelector('p');
        if (title) title.textContent = '이 탭을 닫아주세요';
        if (desc) desc.innerHTML = '기존 탭으로 이동했어요.<br>이 탭은 더 이상 필요 없으니 닫아주세요.';
        btnGoOtherTab.style.display = 'none';
      }, 300);
    });
  }
}

/**
 * 기존 탭에 세션 인계 요청
 * @param {string} code - short code (7자리)
 * @returns {Promise<boolean>} true: 기존 탭이 세션을 인계받음
 */
export function sendTakeSession(code) {
  return new Promise((resolve) => {
    let resolved = false;

    const onMessage = (event) => {
      if (event.data.type === 'session_taken') {
        resolved = true;
        tabChannel.removeEventListener('message', onMessage);
        resolve(true);
      }
    };
    tabChannel.addEventListener('message', onMessage);

    tabChannel.postMessage({ type: 'take_session', code });

    // 3초 내 응답 없으면 실패 (세션 전환에 API 호출 포함되므로 넉넉히)
    setTimeout(() => {
      if (!resolved) {
        tabChannel.removeEventListener('message', onMessage);
        resolve(false);
      }
    }, 3000);
  });
}

/**
 * 탭 채널 참조 반환 (테스트용)
 * @returns {BroadcastChannel}
 */
export function getTabChannel() {
  return tabChannel;
}
